import { computed, ref, type ComputedRef, type Ref } from 'vue'

export type LifecycleState = 'raising' | 'closed' | 'exited'
export type LifecycleFilter = 'all' | LifecycleState

interface UseLifecycleFilterReturn {
  lifecycleFilter: Ref<LifecycleFilter>
  lifecyclePills: { key: LifecycleFilter; label: string }[]
  statusCounts: ComputedRef<Record<LifecycleFilter, number>>
  applyLifecycleFilter: (items: any[]) => any[]
}

// Lifecycle pill bar + filter state. Used by both the SPV and Funds individual
// pages — Funds omits the 'Exited' pill because the BE rejects fund→exited at
// validation in Lifecycleable.
export function useLifecycleFilter(
  items: ComputedRef<any[]> | Ref<any[]>,
  { entityType }: { entityType: 'fund' | 'spv' },
): UseLifecycleFilterReturn {
  const lifecycleFilter = ref<LifecycleFilter>('all')

  const lifecyclePills: { key: LifecycleFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'raising', label: 'Raising' },
    { key: 'closed', label: 'Closed' },
    ...(entityType !== 'fund' ? ([{ key: 'exited' as const, label: 'Exited' }] as const) : []),
  ]

  const statusCounts = computed<Record<LifecycleFilter, number>>(() => {
    let raising = 0
    let closed = 0
    let exited = 0
    for (const row of items.value) {
      if (row.lifecycle_state === 'raising') raising++
      else if (row.lifecycle_state === 'closed') closed++
      else if (row.lifecycle_state === 'exited') exited++
    }
    return { all: items.value.length, raising, closed, exited }
  })

  const applyLifecycleFilter = (xs: any[]) =>
    lifecycleFilter.value === 'all' ? xs : xs.filter((x: any) => x.lifecycle_state === lifecycleFilter.value)

  return { lifecycleFilter, lifecyclePills, statusCounts, applyLifecycleFilter }
}
